import DateUtils from './date-utils'
import { RegionAppPath } from './region-utils'
import { ErrorContextMap } from './sentry/SentryErrorContext'
export const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

export const monthNamesShort = monthNames.map(month => month.slice(0, 3))
export const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export type NonUndefined<T> = T extends undefined ? never : T

/**
 * Extend this class to show your error class as thrown wherever it was instantiated.
 * This is helpful to quickly find the places that our custom errors are thrown.
 */
export abstract class GapErrorBase extends Error {
   constructor(originalError: any) {
      super(typeof originalError === 'string' ? originalError : originalError?.message)
      this.name = this.constructor.name

      Error.captureStackTrace(this, this.constructor)

      if (typeof originalError !== 'string') {
         Object.assign(this, originalError)
      }
   }
}

export class NotNullError extends GapErrorBase {
   constructor(message?: string) {
      super(message)
   }
}

/**
 * NotNull with an options object that can be used to add context to the error i.e. method input parameters and context objects
 * which are useful for Sentry error reporting
 */
export function notNull<T>(
   x: T | undefined | null,
   message = 'Variable should not be undefined or null. Please check the stack to see where the error comes from.',
   options?: {
      // Method parameters for context
      methodParams?: Record<string, any>
      // Context objects must conform to ErrorContextMap
      contextObjects?: Partial<ErrorContextMap>
   }
): T {
   if (typeof x === 'undefined' || x === null) {
      // Create the error
      const error = new NotNullError(message)

      // Make the error trace start from where notNull() was called
      Error.captureStackTrace(error, notNull)

      // Add any provided context directly to the error
      if (options) {
         // Add the entire options object - simple and direct
         const errorWithContext = error as any
         errorWithContext.sentryContext = options
      }

      throw error
   }

   return x as T
}

/**
 * Wraps Promise.all results with null checks for each element
 * @param promiseResults - The results from Promise.all
 * @param messages - Array of error messages for each element (optional)
 * @param options - Additional context options for error reporting
 * @returns Tuple with null-checked values
 */
export function tupleNotNull<T extends readonly unknown[]>(
   promiseResults: T,
   messages?: string[],
   options?: {
      methodParams?: Record<string, any>
      contextObjects?: Partial<ErrorContextMap>
   }
): { [K in keyof T]: NonNullable<T[K]> } {
   return promiseResults.map((result, index) => {
      const message = messages?.[index] || 'Variable should not be undefined or null. Please check the stack to see where the error comes from.'
      return notNull(result, message, options)
   }) as { [K in keyof T]: NonNullable<T[K]> }
}

export function objectIsNull(x: any): boolean {
   return !(Object.keys(x).length > 0)
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** True if `value` is a UUID, distinguishing meetings-workspace ids from ULID work-item ids. */
export function isUuid(value: string | null | undefined): value is string {
   return !!value && UUID_PATTERN.test(value)
}

export function firstNotNull<T>(
   x: (T | undefined | null)[] | undefined | null,
   message = 'Variable should not be undefined or null. Please check the stack to see where the error comes from.'
): T {
   if (typeof x?.[0] === 'undefined' || x?.[0] === null) {
      throw new NotNullError(message)
   }
   return x[0]
}

export function copyFields<T, P extends Array<keyof T>>(obj: T, fields: P): Pick<T, P[number]> {
   return Object.fromEntries(fields.map(key => [key, obj[key]])) as Pick<T, P[number]>
}

/**
 *
 * @param arrayToBeSearched array that contains the key/value pairs
 * @param idToBeSearched id to be searched for
 * @param idField property name of the field to be considered as the key for searching
 * @param fieldPropertyName property name of the field to be returned
 */
export function getFieldById<T, K extends keyof T, P extends keyof T>(params: {
   arrayToBeSearched: T[]
   idToBeSearched: T[K] | undefined
   idField: K
   fieldPropertyName: P
}): T[P] | undefined {
   const item = params.arrayToBeSearched.find(c => c[params.idField] === params.idToBeSearched)
   return item?.[params.fieldPropertyName]
}

/**
 * Formats a number value as currency, including its symbol. Eg 98.9 -> '$98.90'
 */
export const formatCurrency = ({ value, currencyCode }: { value: number; currencyCode: string }) => {
   const formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode, currencyDisplay: 'narrowSymbol' })
   return formatter.format(value)
}

type PromiseResolve<T> = Parameters<ConstructorParameters<typeof Promise<T>>[0]>[0]
type PromiseReject<T> = Parameters<ConstructorParameters<typeof Promise<T>>[0]>[1]

/**
 * A promise that can be settled externally
 */
// Refactor the following class so that an instance can have resolve() called without an argument defined
export class Deferred<T = void> {
   readonly promise: Promise<T>
   private _resolve: PromiseResolve<T>
   private _reject: PromiseReject<T>
   private _settled: boolean

   constructor() {
      this._settled = false
      this.promise = new Promise<T>((resolve, reject) => {
         this._reject = reason => {
            reject(reason)
            this._settled = true
         }

         this._resolve = value => {
            resolve(value)
            this._settled = true
         }
      })
   }

   get settled() {
      return this._settled
   }

   get resolve() {
      return this._resolve
   }

   get reject() {
      return this._reject
   }
}

export const RoutesEnum = [
   'dev-sandbox',
   'my-profile',
   'organisations',
   'roles',
   'background-jobs',
   'document-templates',
   'processes',
   'my-organisation',
   'work-items',
   'documents',
   'delivery-notes',
   'clients',
   'services',
   'proposals',
   'vgc',
   'no-access',
   'mindsets',
   'dev-settings',
   'user-management',
   'meeting-delivery-notes',
   'meetings',
   'coach',
   'feature-toggles',
   'queues',
   'templates',
] as const

type AuthGuardRoute = 'join' | 'client-invitation'

export type RouteType = (typeof RoutesEnum)[number]

// Routes after /au/org/my-org-id
type MenuRoute =
   | RouteType
   | `${RouteType}/${string}`
   | `${RouteType}?${string}`
   | `${RouteType}/${string}?${string}`
   | `${RouteType}/${string}/${string}?${string}`

// Include everything before the menuRoute
export type Route =
   | MenuRoute
   | AuthGuardRoute
   | `/org/${string}/${MenuRoute}`
   | `${string}${RegionAppPath}/${string}/${MenuRoute}`
   | `${string}/org/${string}/${MenuRoute}/${string}`
   | `${string}org/${string}/${MenuRoute}/${string}`
   | `${RegionAppPath}/auth/validate/${string}/${string}?redirectUrl=${string}`
   | `${RegionAppPath}/auth/validate/${string}?redirectUrl=${string}`
   | `${RegionAppPath}/auth/validate/client/${string}/${string}/join`
   | `${RegionAppPath}/auth/update/${string}/${string}`

export type StageEnum =
   | 'dryrun'
   | 'dryrun-v3'
   | 'dev'
   | `dev-${string}`
   | 'preview'
   | `preview-${string}`
   | 'feature'
   | `feature-${string}`
   | 'bug'
   | `bug-${string}`
   | 'test'
   | `integration-testing${string}`
   | 'rc'
   | `rc-${string}`
   | 'preprod'
   | 'prod'

/**
 * This function checks if the order of the 2 given arrays are the same. Ex:
 * const array1 = [
 *    { myId: 1 },
 *    { myId: 2 },
 *    { myId: 3 }
 * ]
 * const array 2 = [
 *    { myId: 2 },
 *    { myId: 1 },
 *    { myId: 3 }
 * ]
 * console.log(arrayHasSameOrder(array1, array2, 'myId')) // Outputs false
 */
export const arrayHasSameOrder = <T extends Record<string, any>, U extends keyof T>(array1: T[], array2: T[], propertyName: U): boolean => {
   return !array1.some((arrayItem, index) => arrayItem[propertyName] !== array2[index][propertyName])
}

/**
 * Extract keys in type that are of a certain given ValueType (ex. boolean / string / number)
 */
export type PickByValue<T, ValueType> = Pick<
   T,
   {
      [Key in keyof T]-?: T[Key] extends ValueType ? Key : never
   }[keyof T]
>

// Basicly a Record, but with optional values
export type PartialRecord<K extends keyof any, T> = {
   [P in K]?: T
}

export const getFullNameFromUser = (user?: NameDto | null) => {
   if (!user) {
      return undefined
   }
   return `${user?.firstName} ${user?.lastName}`
}

export interface NameDto {
   firstName?: string | undefined
   lastName?: string | undefined
}

export const getContactFirstAndLastName = (name: string, firstName: string | undefined, lastName: string | undefined) => {
   if (firstName && firstName.length > 0) {
      return {
         firstName,
         lastName: lastName || '',
      }
   } else {
      if (!name) {
         return {
            firstName: 'firstname',
            lastName: 'lastname',
         }
      }
      const contactNameParts = name.split(' ')
      return {
         firstName: contactNameParts[0] || name,
         lastName: contactNameParts.length > 1 ? contactNameParts.slice(1).join(' ') : '',
      }
   }
}

interface MinimalDocAttributes {
   isBusinessPlan?: boolean
   createdAt: string
   docTypeName?: string
}

export const documentIsBusinessPlanDocument = (document: { isBusinessPlan?: boolean; docTypeName?: string }) => {
   return !!document.isBusinessPlan && (document.docTypeName ? document.docTypeName === 'Delivery' : true)
}

export const sortByCreatedAt = (a: { createdAt: string }, b: { createdAt: string }) => {
   return new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime()
}

type SortFunction<T> = (a: T, b: T) => number
export const sortByAttribute = <T>(attribute: keyof T): SortFunction<T> => {
   return (a: T, b: T) => {
      if (a[attribute] < b[attribute]) return -1
      if (a[attribute] > b[attribute]) return 1
      return 0
   }
}

export const sortByDate = <T>(attribute: keyof T): SortFunction<T> => {
   return (a: T, b: T) => new Date((b[attribute] as string) || '').getTime() - new Date((a[attribute] as string) || '').getTime()
}

export const sortByNumberAttribute = <T>(attribute: keyof T): SortFunction<T> => {
   return (a: T, b: T) => (a[attribute] as number) - (b[attribute] as number)
}

export const getBusinessPlanFromDocumentList = <T extends MinimalDocAttributes>(documents: T[]): T | undefined => {
   return documents.filter(documentIsBusinessPlanDocument).sort(sortByCreatedAt)?.[0]
}

export const kebabCaseToPascalCase = (input: string) =>
   input
      // Replace any a-z character that precedes a hyphen with the uppercase character without the hyphen
      .replace(/(^\w|-\w)/g, match => match.replace('-', '').toUpperCase())

// https://github.com/rigoneri/indefinite-article.js
export const indefiniteArticlePrefix = (text: string | undefined, options?: { isStart: boolean }): 'a' | 'an' | 'A' | 'An' | '' => {
   if (!text) {
      return ''
   }
   // Ensure that if it the start of a sentence we uppercase it
   const format = (input: 'a' | 'an') => {
      if (options?.isStart) {
         return input === 'a' ? 'A' : 'An'
      }
      return input
   }

   // Getting the first word
   const match = /\w+/.exec(text)
   let word = ''
   if (match) {
      word = match[0]
   } else {
      return format('an')
   }

   const lowerCaseWord = word.toLowerCase()
   // Specific start of words that should be preceeded by 'an'
   if (['honest', 'hour', 'hono'].includes(lowerCaseWord)) {
      return format('an')
   }

   // Single letter word which should be preceeded by 'an'
   if (lowerCaseWord.length == 1) {
      return format('aedhilmnorsx'.indexOf(lowerCaseWord) >= 0 ? 'an' : 'a')
   }

   // Capital words which should likely be preceeded by 'an'
   if (word.match(/(?!FJO|[HLMNS]Y.|RY[EO]|SQU|(F[LR]?|[HL]|MN?|N|RH?|S[CHKLMNPTVW]?|X(YL)?)[AEIOU])[FHLMNRSX][A-Z]/)) {
      return format('an')
   }

   // Special cases where a word that begins with a vowel should be preceeded by 'a'
   const regexes = [/^e[uw]/, /^onc?e\b/, /^uni([^nmd]|mo)/, /^u[bcfhjkqrst][aeiou]/]
   if (regexes.find(reggie => lowerCaseWord.match(reggie))) {
      return format('a')
   }

   // Special capital words (UK, UN)
   if (word.match(/^U[NK][AIEO]/)) {
      return format('a')
   } else if (word == word.toUpperCase()) {
      return format('aedhilmnorsx'.indexOf(lowerCaseWord[0]) >= 0 ? 'an' : 'a')
   }

   // Basic method of words that begin with a vowel being preceeded by 'an'
   if ('aeiou'.indexOf(lowerCaseWord[0]) >= 0) {
      return format('an')
   }

   // Instances where y follwed by specific letters is preceeded by 'an'
   if (lowerCaseWord.match(/^y(b[lor]|cl[ea]|fere|gg|p[ios]|rou|tt)/)) return format('an')

   return format('a')
}

export function getDomainFromUrl(urlString?: string) {
   try {
      const url = new URL(notNull(urlString))
      return url.hostname
   } catch {}

   return urlString
}

export function getDomainFromEmail(emailAddress?: string) {
   return emailAddress?.split('@')[1]
}

// To be set as the auth token if no token was found
export const defaultToken = 'OOMPA'

// Default name of a trial org when created. The user will set their own orgname and wont ever see this
export const defaultTrialOrgName = 'TRIAL-ORG'

// Set as default for new actions. We can detect this and leave the field empty when the aciton has just been created, but defaults back to this if they dont change it and reload the page
export const defaultNewActionName = 'New action'

// Add thousand seperator
export const addThousandSeparator = (number: number) => {
   return new Intl.NumberFormat('en-US').format(number)
}

type KeyFromValue<TValue, TType extends Record<PropertyKey, PropertyKey>> = {
   [K in keyof TType]: TValue extends TType[K] ? K : never
}[keyof TType]

type Invert<TType extends Record<PropertyKey, PropertyKey>> = {
   [TValue in TType[keyof TType]]: KeyFromValue<TValue, TType>
}

export function invert<TRecord extends Record<PropertyKey, PropertyKey>>(obj: TRecord): Invert<TRecord> {
   const newObj = Object.create(null)
   for (const key in obj) {
      const v = obj[key]
      newObj[v] = key
   }
   return newObj
}

// Compare 2 interfaces and throw error if mismatch
export type CompareSchemas<T extends U, U extends V, V = T> = any

type PickFrom<T, K extends keyof T> = {
   [P in K]: T[P]
}

export function pickOne<T extends object, K extends keyof T>(obj: T, keys: K[]): PickFrom<T, K> {
   const result = {} as PickFrom<T, K>
   keys.forEach(key => {
      if (key in obj) {
         result[key] = obj[key]
      }
   })
   return result
}

export function pick<T extends object, K extends keyof T>(data: T[], keys: K[]): PickFrom<T, K>[] {
   return data.map(obj => pickOne(obj, keys))
}

export function getDocumentNameForWorkspaceDocument(params: { clientName: string; docTypeName: string; finalisedAt: string }) {
   const fileType = params.docTypeName === 'Delivery' ? 'Session Record' : params.docTypeName
   return `${fileType} ${params.clientName} ${DateUtils.formatDate(params.finalisedAt, 'date-full-month')}`
}

export function getDocumentNameForBusinessPlanDocument(params: { clientName: string; finalisedAt: string }) {
   const finalisedAt = new Date(params.finalisedAt).toISOString().split('T')[0]
   return `Business Plan ${params.clientName} ${finalisedAt}`
}

// Ex. Proposal for Primo Plumbing June 2025
export function getDocumentNameForProposalDocument(params: { clientName: string; sentAt: string }) {
   return `Proposal for ${params.clientName} ${DateUtils.formatMonthAndYear(new Date(params.sentAt))}`
}

export const urlIsVgcRoute = (urlPath: string) => {
   const ulidPattern = '[0-9A-HJKMNP-TV-Z]{26}'
   // Vgc route pattern
   const urlPattern = new RegExp(`^/org/${ulidPattern}/vgc/${ulidPattern}/${ulidPattern}/report$`)

   if (urlPattern.test(urlPath)) {
      const urlSplitted = urlPath.split('/').filter(x => x.length === 26)

      return {
         advisorOrgId: urlSplitted[0],
         clientOrgId: urlSplitted[1],
         vgcId: urlSplitted[2],
      }
   }

   return null
}

export const isGapEmail = (email: string | undefined) => {
   return email?.endsWith('@thegaphq.com')
}

/** The unmonitored subdomain every seeded demo client contact belongs to. */
export const demoContactEmailDomain = 'demo.thegaphq.com'

/**
 * Checks whether an address belongs to a seeded demo client contact (ADR-0004).
 * @param email address to check
 * @returns true for a demo client contact's address
 */
export const isDemoContactEmail = (email: string | undefined): boolean => {
   return !!email?.toLowerCase().endsWith(`@${demoContactEmailDomain}`)
}

/**
 * Whether the given email qualifies the user for the feature-toggle bypass.
 * Gaphq employees see feature-gated UI/endpoints by default, except when
 * their email contains '+featuretoggle' — the opt-out tag used to exercise
 * the non-bypass path in testing.
 */
export const hasFeatureToggleBypass = (email: string | undefined): boolean => {
   return !!email?.endsWith('@thegaphq.com') && !email.includes('+featuretoggle')
}

/** Whether Work Items is visible: the org lacks the `hide-work-items` toggle or the user is a `+featuretoggle` QA account. */
export const canSeeWorkItems = (params: { userEmail: string; featureToggles: readonly string[] }) => {
   return params.userEmail.includes('+featuretoggle') || !params.featureToggles.includes('hide-work-items')
}
